import { boardToScreen, nodeAABB } from '@kanvas/board-core';
import { useMemo } from 'react';
import { useMyVotes, useNodes, useVoteSession, useVoteTotals } from '../collab/hooks';
import type { BoardDoc } from '../collab/BoardDoc';
import type { Identity } from '../lib/identity';
import { useEditor } from '../state/store';

export interface VoteBadgesLayerProps {
  doc: BoardDoc;
  identity: Identity;
}

/**
 * Dot-voting overlay.
 *
 * Rendered as DOM, one badge per node, the same way comment pins are: the
 * canvas never learns votes exist, so nothing in the renderer or hit-testing
 * has to change to support them. Badges only appear while a session is
 * running, or afterwards for nodes that still carry votes — clearing the
 * session (or the whole board) is what makes them disappear for good.
 */
export function VoteBadgesLayer({ doc, identity }: VoteBadgesLayerProps) {
  const nodes = useNodes(doc);
  const viewport = useEditor((s) => s.viewport);
  const session = useVoteSession(doc);
  const totals = useVoteTotals(doc);
  const mine = useMyVotes(doc, identity.userId);

  const used = useMemo(
    () => Object.values(mine).reduce((sum, n) => sum + n, 0),
    [mine],
  );

  const canVote = doc.canComment();

  if (!session.active && totals.size === 0) return null;

  const votable = nodes.filter(
    (n) => n.type !== 'frame' && n.type !== 'group' && n.type !== 'connector' && n.type !== 'comment',
  );

  return (
    <div className="vote-layer">
      {votable.map((node) => {
        const total = totals.get(node.id) ?? 0;
        const myCount = mine[node.id] ?? 0;
        if (!session.active && total === 0) return null;

        const aabb = nodeAABB(node, doc.lookup);
        const p = boardToScreen(viewport, { x: aabb.x + aabb.w, y: aabb.y });
        if (p.x < -60 || p.y < -30 || p.x > viewport.width + 60 || p.y > viewport.height + 30) {
          return null;
        }

        return (
          <div key={node.id} className="vote-badge" style={{ left: p.x, top: p.y }}>
            {total > 0 && <span className="vote-total">{total}</span>}
            {session.active && canVote && (
              <div className="vote-controls" onPointerDown={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  disabled={myCount === 0}
                  aria-label="Убрать голос"
                  onClick={() => doc.castVote(identity.userId, node.id, -1)}
                >
                  −
                </button>
                <span className="vote-mine">{myCount}</span>
                <button
                  type="button"
                  disabled={used >= session.max}
                  aria-label="Проголосовать"
                  onClick={() => doc.castVote(identity.userId, node.id, 1)}
                >
                  +
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
